/*!
 * @Author: Cascade
 * @Date: 2026-02-02
 * @Introduction: OIDC 工具函数
 * @Description: 提供随机字符串生成、URL 参数解析等工具函数
 */

/**
 * 生成加密安全的随机字符串
 * 用于生成 state 和 nonce 参数，防止 CSRF 和重放攻击
 * @param length 字符串长度，默认 32
 * @returns 随机字符串
 */
export function generateRandomString(length: number = 32): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    let result = ''
    
    // 优先使用 crypto API 生成加密安全的随机数
    if (window.crypto && window.crypto.getRandomValues) {
        const randomValues = new Uint32Array(length)
        window.crypto.getRandomValues(randomValues)
        for (let i = 0; i < length; i++) {
            result += charset[randomValues[i] % charset.length]
        }
    } else {
        // 降级方案：使用 Math.random（安全性较低）
        for (let i = 0; i < length; i++) {
            result += charset[Math.floor(Math.random() * charset.length)]
        }
    }
    
    return result
}

/**
 * 解析 URL 查询参数
 * @param url URL 字符串，默认使用当前页面 URL
 * @returns 参数对象
 */
export function parseUrlParams(url?: string): Record<string, string> {
    const searchString = url ? new URL(url).search : window.location.search
    const params = new URLSearchParams(searchString)
    const result: Record<string, string> = {}
    
    params.forEach((value, key) => {
        result[key] = value
    })
    
    return result
}

/**
 * 构建带查询参数的 URL
 * @param baseUrl 基础 URL
 * @param params 查询参数对象
 * @returns 完整 URL
 */
export function buildUrlWithParams(baseUrl: string, params: Record<string, string>): string {
    const url = new URL(baseUrl)
    Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, value)
    })
    return url.toString()
}

/**
 * 从 URL 中移除指定参数并更新浏览器历史记录
 * 用于清除敏感参数（如 code），避免留在浏览器历史记录中
 * @param paramsToRemove 要移除的参数名数组
 */
export function removeUrlParams(paramsToRemove: string[]): void {
    const url = new URL(window.location.href)
    paramsToRemove.forEach(param => {
        url.searchParams.delete(param)
    })
    // 使用 replaceState 替换当前历史记录，不会产生新的历史条目
    window.history.replaceState({}, document.title, url.toString())
}

/**
 * 存储 OIDC state 到 sessionStorage
 * @param state state 值
 */
export function saveOIDCState(state: string): void {
    sessionStorage.setItem('oidc_state', state)
}

/**
 * 获取存储的 OIDC state
 * @returns state 值或 null
 */
export function getOIDCState(): string | null {
    return sessionStorage.getItem('oidc_state')
}

/**
 * 清除存储的 OIDC state
 */
export function clearOIDCState(): void {
    sessionStorage.removeItem('oidc_state')
}

/**
 * 存储 OIDC nonce 到 sessionStorage
 * @param nonce nonce 值
 */
export function saveOIDCNonce(nonce: string): void {
    sessionStorage.setItem('oidc_nonce', nonce)
}

/**
 * 获取存储的 OIDC nonce
 * @returns nonce 值或 null
 */
export function getOIDCNonce(): string | null {
    return sessionStorage.getItem('oidc_nonce')
}

/**
 * 清除存储的 OIDC nonce
 */
export function clearOIDCNonce(): void {
    sessionStorage.removeItem('oidc_nonce')
}

/**
 * 清除所有 OIDC 相关的临时存储
 */
export function clearAllOIDCStorage(): void {
    clearOIDCState()
    clearOIDCNonce()
}
